import type { GameRenderContext, GameRenderer } from "../game";
import type { GoState } from "./types";

export class GoRenderer implements GameRenderer<GoState> {
  #container: HTMLElement | null = null;
  #context: GameRenderContext<GoState> | null = null;

  mount(container: HTMLElement): void {
    this.#container = container;
    container.classList.add("go-board");
    container.setAttribute("aria-label", "Go board");
    container.addEventListener("click", this.#click);
  }

  render(context: GameRenderContext<GoState>): void {
    if (!this.#container) return;
    this.#context = context;
    const { board, size } = context.state;
    const step = 100 / (size - 1);
    const inset = 2.5 + 45 / (size - .1);
    const gridSize = 100 - 2 * inset;
    const labelOffset = -(inset * .65) / gridSize * 100;
    this.#container.style.setProperty("--go-size", String(size));
    this.#container.style.setProperty("--go-step", `${step}%`);
    this.#container.style.setProperty("--go-inset", `${inset}%`);
    this.#container.style.setProperty("--go-point-size", `${54 / (size - 1)}%`);
    this.#container.style.setProperty(
      "--go-label-offset",
      `${labelOffset}%`,
    );

    const files = Array.from({ length: size }, (_, column) => {
      const x = column * step;
      const label = String.fromCharCode(65 + column + (column >= 8 ? 1 : 0));
      return `<span class="go-coordinate go-file" style="--go-x:${x}%">${label}</span>`;
    }).join("");
    const ranks = Array.from({ length: size }, (_, row) => {
      const y = row * step;
      return `<span class="go-coordinate go-rank" style="--go-y:${y}%">${size - row}</span>`;
    }).join("");
    const points = Array.from(board, (stone, action) => {
      const row = Math.floor(action / size);
      const column = action % size;
      const x = column * step;
      const y = row * step;
      const coordinate =
        `${String.fromCharCode(65 + column + (column >= 8 ? 1 : 0))}${size - row}`;
      const classes = [
        "go-point",
        stone === 0 ? "black-stone" : stone === 1 ? "white-stone" : "",
        stone === -1 && context.interactive
          ? `preview-${context.state.currentPlayer === 0 ? "black" : "white"}`
          : "",
        action === context.lastModelAction?.action ? "ai-move" : "",
      ].filter(Boolean).join(" ");
      const occupant = stone === 0 ? ", Black stone" : stone === 1 ? ", White stone" : "";
      const legal = context.interactive && stone === -1;
      return `<button class="${classes}" role="gridcell" data-action="${action}"
        aria-label="${coordinate}${occupant}" title="${coordinate}"
        style="--go-x:${x}%;--go-y:${y}%" ${legal ? "" : "disabled"}></button>`;
    }).join("");

    this.#container.innerHTML =
      `<div class="go-grid">${files}${ranks}${points}</div>`;
  }

  dispose(): void {
    this.#container?.removeEventListener("click", this.#click);
    this.#container?.classList.remove("go-board");
    if (this.#container) this.#container.innerHTML = "";
    this.#container = null;
  }

  #click = (event: Event): void => {
    if (!this.#context?.interactive) return;
    const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (target) this.#context.onAction(Number(target.dataset.action));
  };
}
